// src/services/payment-state.service.ts
export type PaymentState =
  | "idle"
  | "initializing"
  | "processing"
  | "success"
  | "failed"
  | "cancelled";

export interface PaymentStateTransition {
  from: PaymentState[];
  to: PaymentState;
  onTransition?: () => void;
}

export class PaymentStateMachine {
  private state: PaymentState = "idle";
  private listeners: Set<(state: PaymentState) => void> = new Set();

  private transitions: Map<PaymentState, PaymentStateTransition> = new Map([
    ["idle", { from: ["failed", "cancelled", "success"], to: "initializing" }],
    ["initializing", { from: ["idle"], to: "processing" }],
    ["processing", { from: ["initializing"], to: "success" }],
    ["success", { from: ["processing"], to: "idle" }],
    ["failed", { from: ["processing", "initializing"], to: "idle" }],
    ["cancelled", { from: ["processing", "initializing"], to: "idle" }],
  ]);

  get currentState(): PaymentState {
    return this.state;
  }

  subscribe(listener: (state: PaymentState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.state));
  }

  async transition(to: PaymentState): Promise<boolean> {
    const transition = this.transitions.get(to);

    if (!transition || !transition.from.includes(this.state)) {
      console.warn(`Invalid transition from ${this.state} to ${to}`);
      return false;
    }

    this.state = to;
    this.notify();

    if (transition.onTransition) {
      await transition.onTransition();
    }

    return true;
  }

  reset(): void {
    this.state = "idle";
    this.notify();
  }
}

export const paymentStateMachine = new PaymentStateMachine();
